import { stringify } from "qs"
import type { FetchBaseQueryArgs } from "@reduxjs/toolkit/query/react"
import { fetchBaseQuery } from "@reduxjs/toolkit/query/react"
import getBaseUrl from "./getBaseUrl"
import { getTimeZone } from "@holigral/shared/utils"

const paramsSerializer = (params: Record<string, unknown>) => {
  const sanitizedParams: Record<string, unknown> = {}

  Object.keys(params).forEach((paramKey) => {
    const value = params[paramKey]

    if (value === true) {
      // For PHP compatibility
      sanitizedParams[paramKey] = "1"
    } else if (value === false) {
      // For PHP compatibility
      sanitizedParams[paramKey] = "0"
    } else if (value instanceof Date) {
      sanitizedParams[paramKey] = value.toISOString()
    } else {
      sanitizedParams[paramKey] = value
    }
  })

  return stringify(sanitizedParams, {
    arrayFormat: "brackets",
    skipNulls: true,
  })
}

type Options = {
  /** App-specific headers (e.g. authorisation), set before the common ones */
  prepareHeaders?: FetchBaseQueryArgs["prepareHeaders"]
}

/**
 * RTK Query's fetchBaseQuery against our same-origin API: PHP-friendly query
 * parameters and the browser's time zone on every request.
 */
export default function createFetchBaseQuery({ prepareHeaders }: Options = {}) {
  return fetchBaseQuery({
    baseUrl: getBaseUrl(),
    prepareHeaders: async (headers, api) => {
      // The callback may mutate the headers it is handed or return a
      // replacement — RTK Query allows both, so keep whichever came back.
      const prepared = (await prepareHeaders?.(headers, api)) ?? headers
      prepared.set("TimeZone", getTimeZone()) // Unused, but nice to have

      return prepared
    },
    paramsSerializer,
  })
}
